import {
  types,
  ISimpleType,
  onPatch,
  SnapshotIn,
  applyPatch,
  onAction,
  addMiddleware,
  IDisposer,
  getRelativePath,
  IModelType,
  getRoot,
  getSnapshot,
  Instance,
  unprotect
} from 'mobx-state-tree'
import { View } from 'src/api/misc/View'
import { IGenerateDrafter, IJsonPatchInput } from './types'
import { IProject } from 'src/api/models/Project/types'
import { IEditor, IEditorModel } from 'src/api/stores/Editor/types'
import { IRoot } from 'src/api/stores/Root/types'

const Patch = types.frozen<IJsonPatchInput>()

export const generateDrafter: IGenerateDrafter = (model) =>
  model
    .props({
      changes: types.array(Patch),
      inverseChanges: types.array(Patch),
      currentChange: types.optional(types.number, 0)
    })
    .volatile((self) => ({
      skipNextPatch: false as boolean,
      patcher: undefined as undefined | IDisposer,
      changesListener: undefined as undefined | IDisposer
      // patcherConfig: undefined as undefined | 'change' | 'reverse'
    }))
    .actions((self) => {
      const root = getRoot<IRoot>(self)
      return {
        syncEditor() {
          const snapshot = getSnapshot<Instance<IEditor>>(self as $FixMe)
          root.syncEditor(snapshot as any)
        },
        syncProject() {
          const snapshot = getSnapshot<Instance<IProject>>(self.draft)
          root.syncProject(snapshot as any)
        }
      }
    })
    .actions((self) => ({
      setSkipNextPath(bool: boolean) {
        self.skipNextPatch = bool
      },
      record(
        patch: SnapshotIn<typeof Patch>,
        inversePatch: SnapshotIn<typeof Patch>
      ) {
        self.changes.splice(self.currentChange, self.changes.length, patch)
        self.inverseChanges.splice(
          self.currentChange,
          self.inverseChanges.length,
          inversePatch
        )
        ++self.currentChange

        self.skipNextPatch = true
        self.draft.logUpdate()
        self.syncProject()
        self.syncEditor()
      }
    }))
    .views((self) => ({
      get recording() {
        return !!self.patcher
      },
      get undoable() {
        return self.currentChange > 0
      },
      get redoable() {
        return self.currentChange < self.changes.length
      }
    }))
    .actions((self) => {
      return {
        startRecording() {
          self.patcher?.()
          self.patcher = onPatch(self.draft, (patch, reversePatch) => {
            if (self.skipNextPatch) {
              self.setSkipNextPath(false)
              return
            }
            self.record(patch, reversePatch)
            // unprotect(self.draft.updatedAt)
            // self.draft.updatedAt = new Date().valueOf()
          })
        },
        stopRecording() {
          self.patcher?.()
          self.patcher = undefined
        }
        // addChanges(patch, reversePatch) {}
      }
    })
    .actions((self) => ({
      afterAttach() {
        self.changesListener = addMiddleware(
          self.draft,
          (call, next) => {
            let callback = undefined
            if (call.name === 'patch') {
              call.args[2] = getRelativePath(self.draft, call.context)
              callback = ([patch, inversePatch]: [
                IJsonPatchInput,
                IJsonPatchInput
              ]) => self.record(patch, inversePatch)
            }
            if (call.args.length >= 2) {
              const patchArg = call.args[1]
              if (
                typeof patchArg === 'boolean' &&
                !!call.args[1] !== !!self.recording
              ) {
                !!call.args[1] ? self.startRecording() : self.stopRecording()
              }
            }
            next(call, callback)
          },
          false
        )
      },
      beforeDestroy() {
        self.changesListener?.()
        self.patcher?.()
      },
      undo() {
        if (self.undoable) {
          self.skipNextPatch = true
          self.currentChange--
          const patch = self.inverseChanges[self.currentChange]
          applyPatch(self.draft, patch)
          self.syncEditor()
        }
      },
      redo() {
        if (self.redoable) {
          self.skipNextPatch = true
          const patch = self.changes[self.currentChange]
          applyPatch(self.draft, self.changes[self.currentChange])
          self.currentChange++
          self.syncEditor()
        }
      },
      jump(changeIndex: number) {
        self.stopRecording()
        if (changeIndex) {
          self.syncEditor()
        }
      }
    }))

type SecondArgument =
  | boolean
  | [SnapshotIn<typeof Patch>, SnapshotIn<typeof Patch>]
